import React from 'react';
import { LinkMark } from './style';
import useAnnotationMarkPositionStyles from 'components/text-editor/components/annotations/components/hooks/use-annotation-mark-position-styles';
import useTextSnippetSelection from 'components/text-editor/components/controls/hooks/use-selected-text-snippet';
import useCustomEditor from 'components/text-editor/hooks/use-custom-editor';
import { isDefined, notEqual } from 'utils/js';
import { usePrevious } from 'hooks';
import { ReactEditor } from 'slate-react';
import { Node, Transforms } from 'slate';

export interface LinkSnippetMarkProps {
  element: Node;
  text?: string;
}

function LinkSnippetMark(props: LinkSnippetMarkProps) {
  const editor = useCustomEditor();

  const { setSelectedTextSnippet } = useTextSnippetSelection(editor);

  const previousText = usePrevious(props.text);

  const positionStyles = useAnnotationMarkPositionStyles(
    props.element,
    'right',
  ) as any;

  const previousStyles = usePrevious(positionStyles);

  const [lastUpdated, setLastUpdated] = React.useState(
    new Date().toISOString(),
  );

  React.useEffect(() => {
    if (notEqual(positionStyles, previousStyles)) {
      setLastUpdated(new Date().toISOString());
    }
  }, [previousStyles, positionStyles]);

  React.useEffect(() => {
    if (previousText && previousText !== props.text) {
      recalculatePosition();
    }
  }, [previousText, props.text]);

  if (isDefined(positionStyles.left)) {
    positionStyles.left += 0.5;
  }

  function recalculatePosition() {
    // That's the only thing that could force recalculation :(
    Transforms.move(editor, { distance: 1, unit: 'character' });

    Transforms.move(editor, {
      distance: 1,
      unit: 'character',
      reverse: true,
    });
  }

  function selectAnnotation() {
    //@ts-ignore
    const annotationId = props?.element.annotationId;

    if (annotationId) {
      const at = editor.getSlateRangeByAnnotationId(annotationId);

      editor.unMarkNodesAsSelected();

      editor.saveAnnotation(at, { isSelected: true, annotationId });

      const selectedNode = editor.getSelectedNode();

      const domNode = document.getElementById(annotationId);

      if (domNode) {
        const domRange = document.createRange();

        domRange.setStart(domNode, 0);

        domRange.setEnd(domNode, 0);

        const range = ReactEditor.toSlateRange(editor, domRange, {
          exactMatch: false,
        });

        range && Transforms.select(editor, range);
      }

      selectedNode && setSelectedTextSnippet(selectedNode);
    }
  }

  return React.useMemo(() => {
    return (
      <LinkMark
        style={positionStyles}
        contentEditable={false}
        onClick={selectAnnotation}
      />
    );
  }, [lastUpdated, positionStyles]);
}

export default LinkSnippetMark;
